import { faStar as farStar } from "@fortawesome/pro-regular-svg-icons";
import {
    faPause,
    faPencil,
    faPlay,
    faSync,
    faTrash,
    faStar as fasStar,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import range from "lodash-es/range";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { analyticsStoreEvent } from "@/mk2/actions";
import DeletedReasonForm, {
    DeletedReasonFormData,
} from "@/mk2/apps/bazaar/components/DeletedReasonForm";
import {
    DeletedReason,
    ProductEntity,
    ProductQuality,
    RepostableStatus,
} from "@/mk2/apps/bazaar/schemas";
import { NoteEntity } from "@/mk2/apps/notes/schemas";
import { wikiAddExperienceToUndefinedArticleUrl } from "@/mk2/apps/wiki/urls";
import { Btn, BtnType } from "@/mk2/components/Btn";
import { Link } from "@/mk2/components/Link";
import { Panel } from "@/mk2/components/Panel";
import { abFetch } from "@/mk2/containers/AB/AB.actions";
import { getABVariant } from "@/mk2/containers/AB/AB.selectors";
import { url } from "@/mk2/helpers/urls";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import { AppState } from "@/mk2/reducers";
import { isSuccess } from "@/mk2/reducers/loadingState";
import {
    ACTIVATE_ENTRY,
    BAZAAR_NOTES_ADD_NOTE,
    BAZAAR_NOTES_TO_NOTES,
    BZ2_PRODUCT_DELETE_CONFIRM,
    BZ2_REVIEW_HOOK_BUTTON,
    BZ2_REVIEW_HOOK_PROMPT,
    CHANGE_ENTRY,
    DELETE_ENTRY,
    INACTIVATE_ENTRY,
    REPOST_ENTRY,
    SELLER_ACTION_BUTTONS_UNDELETE,
    SELLER_ACTION_BUTTONS_UNDELETE_CONFIRM,
    YOU_HAVE_NO_MORE_REPOSTS_SHORT,
} from "@/mk/autogenerated/translations/SellerActionButtons.e6608bb2de8bc7d08104bf1fe2153e68";
import { productSlugToArray } from "@/mk/bazaar/product/utils";
import {
    adminclub_bazaar_product_url,
    adminclub_user_notes_url,
    adminclub_users_url,
    bazaar_product_history_url,
    bazaar_single_page_modify_product_url,
} from "@/mk/urls/functions";

import styles from "./SellerActionButtons.mscss";

const REVIEW_HOOK_AB = "bazaar_review_hook_label";

interface OwnProps {
    className?: string;
    product: ProductEntity;
    isSeller?: boolean;
    isAdmin?: boolean;
    isModerator?: boolean;
    lastNote?: NoteEntity;
    historyCount?: number;

    isActivating?: boolean;
    isDeactivating?: boolean;
    isDeleting?: boolean;
    isUndeleting?: boolean;
    isReposting?: boolean;

    onActivate(event: React.MouseEvent<HTMLAnchorElement>);
    onDeactivate(event: React.MouseEvent<HTMLAnchorElement>);
    onDelete(reason?: DeletedReason);
    onUndelete();
    onRepost(event: React.MouseEvent<HTMLAnchorElement>);
}

type Props = OwnProps;

const SellerActionButtons: React.FunctionComponent<Props> = ({
    className,
    product,
    isSeller = false,
    isAdmin = false,
    isModerator = false,
    lastNote = null,
    historyCount = 0,
    onActivate,
    onDeactivate,
    onRepost,
    onDelete,
    onUndelete,
    isActivating = false,
    isDeactivating = false,
    isDeleting = false,
    isReposting = false,
    isUndeleting = false,
}) => {
    const [showDeletedReasons, setShowDeletedReasons] = useState<boolean>(false);
    const [hoveredStar, setHoveredStar] = useState<number>(0);

    const dispatch = useDispatch();
    const reviewAb = useSelector((state: AppState) => getABVariant(state, REVIEW_HOOK_AB));
    const showReviewHook =
        isSeller && product.quality === ProductQuality.OLD && !product.state.isDeleted;

    useEffect(() => {
        // the A/B variant is per-user, so fetching once when the hook becomes
        // relevant is enough; depending on reviewAb here would re-fetch in a loop
        if (showReviewHook && !isSuccess(reviewAb.loadingState)) {
            dispatch(abFetch(REVIEW_HOOK_AB));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showReviewHook]);

    const buildReviewUrl = (rating?: number) =>
        url(
            wikiAddExperienceToUndefinedArticleUrl,
            {},
            {
                fromProduct: product.id,
                ...(product.title ? { articleProposal: product.title } : {}),
                ...(rating ? { rating } : {}),
            },
        );

    const onReviewHookClick = (event: React.MouseEvent, rating?: number) => {
        // Send the conversion event first and let the saga navigate once the POST
        // lands; a plain link would race the navigation and drop the event on a
        // native page load.
        event.preventDefault();
        dispatch(
            analyticsStoreEvent(
                "bazaar_review_hook_click",
                {
                    variant: reviewAb.variant,
                    productId: product.id,
                    ...(rating ? { rating } : {}),
                },
                buildReviewUrl(rating),
            ),
        );
    };

    const onDeleteCallback = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            if ((isAdmin || isModerator) && !isSeller) {
                setShowDeletedReasons(true);
            } else {
                if (!confirm(BZ2_PRODUCT_DELETE_CONFIRM)) {
                    return;
                }

                onDelete();
            }
        },
        [onDelete, isAdmin, isModerator, setShowDeletedReasons],
    );

    const onUndeleteCallback = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            if (!confirm(SELLER_ACTION_BUTTONS_UNDELETE_CONFIRM)) {
                return;
            }
            onUndelete();
        },
        [onUndelete],
    );

    const onCancelDeleteForm = useEventCallback(
        (event: React.MouseEvent<HTMLAnchorElement>) => {
            setShowDeletedReasons(false);
        },
        [setShowDeletedReasons],
    );

    const onSaveDeletedReason = useEventCallback(
        (values: DeletedReasonFormData) => {
            onDelete(values.reason);
            setShowDeletedReasons(false);
        },
        [onDelete, setShowDeletedReasons],
    );

    const adminActions = (
        <>
            {historyCount > 0 && (
                <Link
                    className={styles.SellerActionButtons__history}
                    to={bazaar_product_history_url(product.id)}
                    target="_blank"
                >
                    History ({historyCount} changes)
                </Link>
            )}
            {isAdmin && (
                <Link
                    className={styles.SellerActionButtons__adminclub}
                    to={adminclub_bazaar_product_url(product.id)}
                    target="_blank"
                >
                    Adminclub (product)
                </Link>
            )}
            {isAdmin && product.seller?.username && (
                <Link
                    className={styles.SellerActionButtons__adminclub}
                    to={adminclub_users_url({ user: product.seller.username })}
                    target="_blank"
                >
                    Adminclub (user)
                </Link>
            )}
        </>
    );

    if (product.state.isDeleted) {
        return isAdmin ? (
            <Panel
                className={cx(
                    styles.SellerActionButtons,
                    className,
                    isAdmin && styles["SellerActionButtons--admin"],
                )}
            >
                {new Date(product.endTime).getTime() > Date.now() - 24 * 60 * 60 * 1000 && (
                    <div className={styles.SellerActionButtons__top}>
                        <Btn
                            className={styles.SellerActionButtons__top__button}
                            type={BtnType.AdminOutline}
                            icon={faTrash}
                            onClick={onUndeleteCallback}
                            hasSpinner={isUndeleting}
                            label={SELLER_ACTION_BUTTONS_UNDELETE}
                        />
                    </div>
                )}
                {adminActions}
            </Panel>
        ) : null;
    }

    const category = product.properties.find((p) => p[0] === "cat")[1];

    return (
        <Panel
            className={cx(
                styles.SellerActionButtons,
                className,
                isAdmin && styles["SellerActionButtons--admin"],
                isModerator && styles["SellerActionButtons--moderator"],
            )}
        >
            <div className={styles.SellerActionButtons__top}>
                <Btn
                    className={styles.SellerActionButtons__top__button}
                    type={BtnType.BlueOutline}
                    icon={faPencil}
                    label={CHANGE_ENTRY}
                    link={bazaar_single_page_modify_product_url(
                        ...productSlugToArray(product.slug),
                    )}
                />{" "}
                {(isSeller || isAdmin) &&
                    !product.seller?.hasBlockedBazaar &&
                    (product.state.isInactive || product.state.isActive) && (
                        <>
                            <Btn
                                className={styles.SellerActionButtons__top__button}
                                type={BtnType.BlueOutline}
                                icon={product.state.isActive ? faPause : faPlay}
                                onClick={product.state.isActive ? onDeactivate : onActivate}
                                label={product.state.isActive ? INACTIVATE_ENTRY : ACTIVATE_ENTRY}
                                hasSpinner={product.state.isActive ? isDeactivating : isActivating}
                            />{" "}
                        </>
                    )}
                <Btn
                    className={styles.SellerActionButtons__top__button}
                    type={BtnType.BlueOutline}
                    icon={faTrash}
                    onClick={onDeleteCallback}
                    hasSpinner={isDeleting}
                    label={
                        <>
                            {DELETE_ENTRY}
                            {!isSeller && <>&hellip;</>}
                        </>
                    }
                />
            </div>

            {showReviewHook && reviewAb.variant === "button" && (
                <div className={styles.SellerActionButtons__reviewHook}>
                    <div className={styles.SellerActionButtons__reviewHook__prompt}>
                        {BZ2_REVIEW_HOOK_PROMPT}
                    </div>
                    <Btn
                        className={styles.SellerActionButtons__reviewHook__button}
                        type={BtnType.BlueOutline}
                        label={BZ2_REVIEW_HOOK_BUTTON}
                        link={buildReviewUrl()}
                        onClick={(e) => onReviewHookClick(e)}
                    />
                </div>
            )}

            {showReviewHook && reviewAb.variant === "starsrating" && (
                <div className={styles.SellerActionButtons__reviewHook}>
                    <div className={styles.SellerActionButtons__reviewHook__prompt}>
                        {BZ2_REVIEW_HOOK_PROMPT}
                    </div>
                    <div
                        className={styles.SellerActionButtons__reviewHook__stars}
                        onMouseLeave={() => setHoveredStar(0)}
                    >
                        {range(1, 6).map((rating) => (
                            <Link
                                key={rating}
                                className={styles.SellerActionButtons__reviewHook__star}
                                to={buildReviewUrl(rating)}
                                onClick={(e) => onReviewHookClick(e, rating)}
                                onMouseEnter={() => setHoveredStar(rating)}
                            >
                                <FontAwesomeIcon icon={rating <= hoveredStar ? fasStar : farStar} />
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {(isAdmin || isModerator) &&
                product.seller &&
                (lastNote ? (
                    <div className={styles.SellerActionButtons__notes}>
                        {lastNote.note}

                        <Link
                            className={styles.SellerActionButtons__notes__change}
                            to={
                                isAdmin
                                    ? adminclub_users_url({ user: product.seller.username })
                                    : adminclub_user_notes_url({ user: product.seller.username })
                            }
                            target="_blank"
                        >
                            {BAZAAR_NOTES_TO_NOTES}
                        </Link>
                    </div>
                ) : (
                    <Link
                        className={styles.SellerActionButtons__no_notes}
                        to={
                            isAdmin
                                ? adminclub_users_url({ user: product.seller.username })
                                : adminclub_user_notes_url({ user: product.seller.username })
                        }
                        target="_blank"
                    >
                        {BAZAAR_NOTES_ADD_NOTE}
                    </Link>
                ))}

            {isSeller &&
                !product.seller?.hasBlockedBazaar &&
                product.repostableStatus === RepostableStatus.REPOSTABLE_FREE && (
                    <div className={styles.SellerActionButtons__repost}>
                        <Btn
                            className={styles.SellerActionButtons__repost__button}
                            type={BtnType.Blue}
                            icon={faSync}
                            onClick={onRepost}
                            disabled={!product.seller?.availableReposts}
                            label={
                                product.seller?.availableReposts
                                    ? REPOST_ENTRY
                                    : YOU_HAVE_NO_MORE_REPOSTS_SHORT
                            }
                            hasSpinner={isReposting}
                        />
                    </div>
                )}

            {showDeletedReasons && (
                <DeletedReasonForm
                    onCancel={onCancelDeleteForm}
                    onSave={onSaveDeletedReason}
                    alreadyDeleted={product.state.isDeleted}
                />
            )}

            {adminActions}
        </Panel>
    );
};

SellerActionButtons.displayName = "SellerActionButtons";

export default React.memo<Props>(SellerActionButtons);
